import { Alert, AlertTitle, Box, Button, Stack, Typography } from '@mui/material'
import { useRef, useState } from 'react'
import ActionArgsTable from '@/features/copilot/ActionArgsTable'

export interface ProposedAction {
  type: 'proposedAction'
  action: string
  args: Record<string, unknown>
  toolCallId?: string
  summary?: string
  // HMAC token the server stamps on every proposedAction (confirm.ts /
  // chat.ts attachConfirmToken). The widget MUST echo it as the 4th field of
  // the CONFIRM_TOOL marker so the server can verify the confirmation was for
  // an action it actually proposed. Absent in dev/eval (secret unset).
  confirmToken?: string
}

export const isProposedAction = (value: unknown): value is ProposedAction => {
  if (!value || typeof value !== 'object') return false
  const v = value as Record<string, unknown>
  return (
    v.type === 'proposedAction' &&
    typeof v.action === 'string' &&
    typeof v.args === 'object' &&
    v.args !== null
  )
}

interface ProposedActionCardProps {
  action: ProposedAction
  onConfirm: (action: ProposedAction) => void
  onCancel?: () => void
  disabled?: boolean
  alreadyConfirmed?: boolean
}

type ActionStatus = 'pending' | 'confirmed' | 'cancelled'

const ProposedActionCard = ({
  action,
  onConfirm,
  onCancel,
  disabled = false,
  alreadyConfirmed = false,
}: ProposedActionCardProps) => {
  const [status, setStatus] = useState<ActionStatus>('pending')
  // Synchronous mirror of `status` to guard against rapid double-clicks
  // (two clicks before React re-renders both see status='pending' from the
  // stale closure and fire onConfirm twice).
  const statusRef = useRef<ActionStatus>('pending')

  const handleConfirm = () => {
    if (statusRef.current !== 'pending') return
    statusRef.current = 'confirmed'
    setStatus('confirmed')
    try {
      onConfirm(action)
    } catch (error) {
      console.error('Failed to confirm proposed action:', error)
      statusRef.current = 'pending'
      setStatus('pending')
    }
  }

  const handleCancel = () => {
    if (statusRef.current !== 'pending') return
    statusRef.current = 'cancelled'
    setStatus('cancelled')
    onCancel?.()
  }

  if (status === 'cancelled') {
    return (
      <Alert severity="info" variant="outlined" sx={{ fontSize: 13 }}>
        <Typography variant="body2" sx={{ fontSize: 13 }}>
          Cancelled. No changes were made.
        </Typography>
      </Alert>
    )
  }

  if (status === 'confirmed') {
    return (
      <Alert severity="success" variant="outlined" sx={{ fontSize: 13 }}>
        <Typography variant="body2" sx={{ fontSize: 13 }}>
          Confirmation sent. Executing action...
        </Typography>
      </Alert>
    )
  }

  // Equivalent action already confirmed elsewhere in this conversation
  // (LLM re-emitted proposedAction after a successful tool run). Surface an
  // info banner instead of a second Confirm button so the user can't fire
  // the same tool twice.
  if (alreadyConfirmed) {
    return (
      <Alert severity="info" variant="outlined" sx={{ fontSize: 13 }}>
        <Typography variant="body2" sx={{ fontSize: 13 }}>
          Already confirmed earlier in this conversation.
        </Typography>
      </Alert>
    )
  }

  return (
    <Alert
      severity="warning"
      variant="outlined"
      sx={{ fontSize: 13, '& .MuiAlert-message': { width: '100%' } }}
    >
      <AlertTitle sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>
        Confirm action
      </AlertTitle>
      <Typography variant="body2" sx={{ fontSize: 13, mb: 1 }}>
        {action.summary ?? `Action: ${action.action}`}
      </Typography>
      <Box
        component="details"
        sx={{
          mb: 1,
          '& > summary': {
            cursor: 'pointer',
            fontSize: 11.5,
            color: 'grey.600',
            userSelect: 'none',
            py: 0.25,
            outline: 'none',
            '&:hover': { color: 'text.main' },
          },
        }}
      >
        <Box component="summary">Show details</Box>
        <Box
          sx={{
            mt: 0.5,
            p: 1,
            borderRadius: 1,
            bgcolor: 'action.hover',
          }}
        >
          <ActionArgsTable data={action.args} />
        </Box>
      </Box>
      <Stack direction="row" spacing={1}>
        <Button
          size="small"
          variant="contained"
          color="warning"
          onClick={handleConfirm}
          disabled={disabled}
        >
          Confirm
        </Button>
        <Button
          size="small"
          variant="outlined"
          color="inherit"
          onClick={handleCancel}
          disabled={disabled}
        >
          Cancel
        </Button>
      </Stack>
    </Alert>
  )
}

export default ProposedActionCard
